import {
  Alert,
  Button,
  Container,
  List,
  Text,
  Title,
  Typography,
} from "@mantine/core";
import { IconLogin2, IconMailCheck } from "@tabler/icons-react";
import { Link, useLocation } from "react-router-dom";
import { z } from "zod";
import { useForwardIfLoggedIn } from "../hooks/useForwardIfLoggedIn.ts";
import { CandidateContact } from "../components/CandidateContact.tsx";

/**
 * `useLocation().state` ist bei React Router `any`. Statt zu casten wird der
 * Wert geparst — so bleibt die Seite auch dann heil, wenn jemand die Adresse
 * direkt aufruft und gar kein State existiert.
 */
const navigationState = z.object({
  email: z.string(),
  fieldsOfStudyCount: z.number(),
});

export const RegistrationComplete = () => {
  useForwardIfLoggedIn("/bewerbung");

  /**
   * Die Registrierung reicht die verwendete Adresse und die Zahl der gewaehlten
   * Studienrichtungen weiter. Die Adresse zu zeigen faengt den haeufigsten
   * Fehler ab: einen Tippfehler in der eigenen Mailadresse — der faellt sonst
   * erst auf, wenn tagelang keine Mail kommt.
   */
  const state = navigationState.safeParse(useLocation().state);
  const email = state.success ? state.data.email : undefined;

  /*
    Zwei Studienrichtungen sind das Maximum. Wer schon zwei gewaehlt hat, dem
    eine zweite anzubieten, waere schlicht falsch — und wer die Seite direkt
    aufruft, bekommt die Zeile lieber gar nicht als eine womoeglich unzutreffende.
  */
  const kannWeitereStudienrichtung =
    state.success && state.data.fieldsOfStudyCount < 2;

  return (
    <Container my="lg">
      <Typography>
        <Title mb="lg">
          Fast geschafft — bitte bestätige deine E-Mail-Adresse
        </Title>

        <Alert color="green" icon={<IconMailCheck />} mb="lg">
          Wir haben dir eine Mail{email ? ` an ${email}` : ""} geschickt. Klick
          auf den Link darin, dann ist deine Registrierung abgeschlossen.
        </Alert>

        <Text>Danach kannst du dich einloggen und im Portal:</Text>
        {/*
          Die Satzzeichen haengen am Eintrag, nicht am Text — sonst endet die
          Liste auf einem Komma, sobald der letzte Eintrag entfaellt.
        */}
        <List>
          {[
            "deine digitale Mappe hochladen",
            "die Hausaufgabe abgeben",
            ...(kannWeitereStudienrichtung
              ? ["dich zusätzlich für eine zweite Studienrichtung bewerben"]
              : []),
          ].map((eintrag, index, alle) => (
            <List.Item key={eintrag}>
              {eintrag}
              {index == alle.length - 1 ? "." : ","}
            </List.Item>
          ))}
        </List>

        <Text>
          <strong>Keine Mail bekommen?</strong> Schau bitte im Spam-Ordner nach.
          Wenn dort auch nichts liegt, melde dich bei <CandidateContact />.
        </Text>
      </Typography>

      {/*
        Der Knopf steht ausserhalb von `Typography`: das faerbt jedes <a> in der
        Anchor-Farbe, und ein Button mit `component={Link}` ist ein <a> — die
        Beschriftung war dadurch blau auf blau.

        Vorher endete die Seite mit „Diese Seite kannst du schließen." — ein
        Schlusspunkt ohne Weg. Wer die Mail schon bestaetigt hat oder es gleich
        danach versucht, braucht hier den naechsten Schritt.
      */}
      <Button
        color="blue"
        size="lg"
        component={Link}
        to="/login"
        leftSection={<IconLogin2 />}
        mt="lg"
      >
        Zum Login
      </Button>
    </Container>
  );
};

export default RegistrationComplete;
